import React, { useState, useRef, useEffect } from 'react';
import { X, Send, User, Bot } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Willkommen bei LIVAG! Wie kann ich Ihnen helfen? Stellen Sie mir eine Frage zu unseren Immobilien, Projekten oder Dienstleistungen.',
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactData, setContactData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userQuestion, setUserQuestion] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Generate a brief, generic response based on keywords
  const generateInitialResponse = (question) => {
    const q = question.toLowerCase();
    
    if (q.includes('preis') || q.includes('kosten') || q.includes('chf') || q.includes('franken')) {
      return 'Die Preise variieren je nach Projekt, Lage und Ausstattung. Unsere Objekte bieten ein ausgezeichnetes Preis-Leistungs-Verhältnis.';
    }
    if (q.includes('wohnung') || q.includes('apartment')) {
      return 'Wir haben verschiedene Wohnungen in unterschiedlichen Grössen und Lagen im Angebot. Von kompakten Stadtwohnungen bis zu grosszügigen Familienwohnungen.';
    }
    if (q.includes('haus') || q.includes('einfamilienhaus') || q.includes('villa')) {
      return 'Unsere Häuser zeichnen sich durch hochwertige Bauweise und durchdachte Grundrisse aus. Wir entwickeln Projekte in der gesamten Ostschweiz.';
    }
    if (q.includes('st. gallen') || q.includes('st.gallen') || q.includes('gossau') || q.includes('rorschach') || q.includes('herisau') || q.includes('wil')) {
      return 'Die Ostschweiz ist unser Kerngebiet. Wir kennen den lokalen Markt sehr gut und haben attraktive Projekte in verschiedenen Gemeinden.';
    }
    if (q.includes('projekt') || q.includes('neubau') || q.includes('bau')) {
      return 'Wir entwickeln nachhaltige Immobilienprojekte mit Fokus auf Qualität und Werterhalt. Aktuell haben wir mehrere spannende Projekte in Entwicklung.';
    }
    if (q.includes('kauf') || q.includes('kaufen') || q.includes('erwerb')) {
      return 'Der Immobilienkauf ist eine wichtige Entscheidung. Wir begleiten Sie gerne durch den gesamten Prozess – von der ersten Besichtigung bis zum Notartermin.';
    }
    if (q.includes('miete') || q.includes('mieten') || q.includes('vermietung')) {
      return 'Neben dem Verkauf bieten wir auch ausgewählte Mietobjekte an. Unsere Mietwohnungen überzeugen durch moderne Ausstattung und gute Lagen.';
    }
    if (q.includes('besichtigung') || q.includes('termin') || q.includes('besuch')) {
      return 'Gerne organisieren wir eine persönliche Besichtigung für Sie. So können Sie sich selbst ein Bild von der Qualität unserer Objekte machen.';
    }
    if (q.includes('finanzierung') || q.includes('hypothek') || q.includes('kredit')) {
      return 'Bei Finanzierungsfragen arbeiten wir mit erfahrenen Partnern zusammen, die Ihnen attraktive Konditionen anbieten können.';
    }
    if (q.includes('kontakt') || q.includes('erreich') || q.includes('telefon') || q.includes('mail')) {
      return 'Sie erreichen uns telefonisch oder per E-Mail. Unser Team steht Ihnen gerne für alle Fragen zur Verfügung.';
    }
    
    // Default response
    return 'Vielen Dank für Ihre Anfrage. Als Immobilienentwickler in der Ostschweiz bieten wir Ihnen professionelle Beratung und hochwertige Objekte.';
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage = { type: 'user', text: inputValue };
    setMessages((prev) => [...prev, userMessage]);
    setUserQuestion(inputValue);
    const question = inputValue;
    setInputValue('');

    // First: Brief initial response after short delay
    setTimeout(() => {
      const initialResponse = {
        type: 'bot',
        text: generateInitialResponse(question),
      };
      setMessages((prev) => [...prev, initialResponse]);
      
      // Second: Referral message after another delay
      setTimeout(() => {
        const referralResponse = {
          type: 'bot',
          text: 'Wir haben Ihre Anfrage an einen Spezialisten der LIVAG weitergeleitet. Bitte ergänzen Sie Ihre E-Mail, Vorname und Name sowie Telefonnummer. Wir werden uns umgehend unverbindlich melden.',
        };
        setMessages((prev) => [...prev, referralResponse]);
        setShowContactForm(true);
      }, 1500);
    }, 800);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send to backend
      const response = await fetch(`${API_URL}/api/chatbot-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: userQuestion,
          ...contactData,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setMessages((prev) => [
          ...prev,
          {
            type: 'bot',
            text: `Vielen Dank, ${contactData.firstName}! Wir haben Ihre Anfrage erhalten und werden uns in Kürze bei Ihnen melden.`,
          },
        ]);
        setShowContactForm(false);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          text: 'Es gab ein Problem beim Senden Ihrer Anfrage. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter info@livag.ch',
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetChat = () => {
    setMessages([
      {
        type: 'bot',
        text: 'Willkommen bei LIVAG! Wie kann ich Ihnen helfen? Stellen Sie mir eine Frage zu unseren Immobilien, Projekten oder Dienstleistungen.',
      },
    ]);
    setShowContactForm(false);
    setSubmitted(false);
    setContactData({ email: '', firstName: '', lastName: '', phone: '' });
    setUserQuestion('');
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${
          isOpen ? 'w-14 h-14 rounded-full bg-gray-600 shadow-lg flex items-center justify-center' : ''
        }`}
        aria-label={isOpen ? 'Chat schliessen' : 'Chat öffnen'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <img 
            src="https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/dbfsynkx_Chatbot_Baukrahn_WebP.webp"
            alt="LIVAG Chatbot"
            className="w-24 h-24 sm:w-32 sm:h-32 object-contain hover:scale-110 transition-transform duration-300 drop-shadow-lg cursor-pointer"
          />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[350px] sm:w-[380px] bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200 animate-in slide-in-from-bottom-5">
          {/* Header */}
          <div className="bg-[#264F77] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">LIVAG Assistent</h3>
                <p className="text-xs text-gray-200">Wir helfen Ihnen gerne</p>
              </div>
            </div>
            {submitted && (
              <button
                onClick={resetChat}
                className="text-xs bg-white/20 px-3 py-1 rounded-full hover:bg-white/30 transition-colors"
              >
                Neuer Chat
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="h-[300px] overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2 rounded-2xl text-sm ${
                    message.type === 'user'
                      ? 'bg-[#264F77] text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}

            {/* Contact Form */}
            {showContactForm && !submitted && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <form onSubmit={handleContactSubmit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Vorname *"
                      required
                      value={contactData.firstName}
                      onChange={(e) =>
                        setContactData({ ...contactData, firstName: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Name *"
                      required
                      value={contactData.lastName}
                      onChange={(e) =>
                        setContactData({ ...contactData, lastName: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none"
                    />
                  </div>
                  <input
                    type="email"
                    placeholder="E-Mail *"
                    required
                    value={contactData.email}
                    onChange={(e) =>
                      setContactData({ ...contactData, email: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none"
                  />
                  <input
                    type="tel"
                    placeholder="Telefonnummer *"
                    required
                    value={contactData.phone}
                    onChange={(e) =>
                      setContactData({ ...contactData, phone: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#264F77] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#1e3d5c] transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
                  </button>
                </form>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {!showContactForm && !submitted && (
            <div className="p-4 border-t border-gray-100 bg-white">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ihre Frage eingeben..."
                  className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-full focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim()}
                  className="w-10 h-10 bg-[#264F77] text-white rounded-full flex items-center justify-center hover:bg-[#1e3d5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Footer for submitted state */}
          {submitted && (
            <div className="p-4 border-t border-gray-100 bg-white text-center">
              <p className="text-xs text-gray-500">
                Vielen Dank für Ihre Anfrage!
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};
